import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Spin, message } from "antd";
import { DownloadOutlined, SaveOutlined } from "@ant-design/icons";
import {
  fetchHealthClientHistory,
  fetchHealthHistoryTemplate,
  saveHealthClientHistory,
  saveHealthHistoryTemplate
} from "./health-clients-api";
import ClientHistoryEditor from "./client-history-editor";
import { sectionsSnapshot } from "./client-history-doc";
import { pick, toNumber } from "./health-clients-utils";

const normalizeSection = (row) => ({
  idPart: toNumber(pick(row, "idPart", "IdPart")),
  name: pick(row, "name", "Name") ?? "",
  value: pick(row, "fValue", "FValue", "value", "Value") ?? ""
});

const mergeDraftValues = (catalog, draft) => {
  const byId = new Map((draft || []).map((s) => [s.idPart, s.value ?? ""]));
  return (catalog || []).map((s) => ({
    ...s,
    value: byId.has(s.idPart) ? byId.get(s.idPart) : s.value
  }));
};

/**
 * Вкладка «История болезни»: WYSIWYG с фиксированными разделами.
 */
const ClientHistory = ({ idClient, open = true }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [draftSections, setDraftSections] = useState([]);
  const [savedSnap, setSavedSnap] = useState("[]");
  const [editorKey, setEditorKey] = useState(0);
  const [templateAvailable, setTemplateAvailable] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const hydratingRef = useRef(false);

  const dirty = sectionsSnapshot(draftSections) !== savedSnap;

  const refreshTemplateFlag = useCallback(() => {
    fetchHealthHistoryTemplate()
      .then((data) => {
        const flag = pick(data, "available", "Available");
        const sections = pick(data, "sections", "Sections") || [];
        const hasText = (Array.isArray(sections) ? sections : []).some((s) =>
          String(pick(s, "fValue", "FValue", "value", "Value") ?? "").trim()
        );
        // available из API или есть непустые разделы типовой формы
        setTemplateAvailable(flag === true || flag === 1 || hasText);
      })
      .catch(() => setTemplateAvailable(false));
  }, []);

  const load = useCallback(() => {
    if (!idClient || open === false) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchHealthClientHistory(idClient)
      .then((data) => {
        if (cancelled) return;
        const sections = (Array.isArray(data) ? data : []).map(normalizeSection).filter((s) => s.idPart != null);
        hydratingRef.current = true;
        setCatalog(sections);
        setDraftSections(sections);
        setSavedSnap(sectionsSnapshot(sections));
        setEditorKey((k) => k + 1);
        refreshTemplateFlag();
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || "Не удалось загрузить историю болезни");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [idClient, open, refreshTemplateFlag]);

  useEffect(() => load(), [load]);

  const handleEditorChange = (sections) => {
    const merged = mergeDraftValues(catalog, sections);
    if (hydratingRef.current) {
      hydratingRef.current = false;
      setDraftSections(merged);
      setSavedSnap(sectionsSnapshot(merged));
      return;
    }
    setDraftSections(merged);
  };

  const handleSave = async () => {
    if (!idClient || saving) return;

    // Каждый раздел — отдельная запись { idPart, value }.
    const payload = catalog.map((s) => {
      const draft = draftSections.find((d) => d.idPart === s.idPart);
      return { idPart: s.idPart, value: draft?.value ?? "" };
    });

    setSaving(true);
    try {
      const data = await saveHealthClientHistory(idClient, payload);
      const sections = (Array.isArray(data) ? data : []).map(normalizeSection).filter((s) => s.idPart != null);
      hydratingRef.current = true;
      setCatalog(sections);
      setDraftSections(sections);
      setSavedSnap(sectionsSnapshot(sections));
      setEditorKey((k) => k + 1);
      message.success("История болезни сохранена");
    } catch (e) {
      message.error(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    hydratingRef.current = true;
    setDraftSections(catalog);
    setEditorKey((k) => k + 1);
  };

  /** Как BtnLoadDefault: подставить тексты id_client=0 в редактор (без записи в БД клиента). */
  const handleLoadTemplate = () => {
    Modal.confirm({
      title: "Загрузить типовую форму?",
      content: "Текущий текст в редакторе будет заменён типовым. Сохранение — отдельно.",
      okText: "Загрузить",
      cancelText: "Отмена",
      onOk: async () => {
        setTemplateLoading(true);
        try {
          const data = await fetchHealthHistoryTemplate();
          const available = Boolean(pick(data, "available", "Available"));
          setTemplateAvailable(available);
          if (!available) {
            message.warning("Типовая форма не задана");
            return;
          }
          const templateSections = (pick(data, "sections", "Sections") || [])
            .map(normalizeSection)
            .filter((s) => s.idPart != null);
          const byId = new Map(templateSections.map((s) => [s.idPart, s.value ?? ""]));
          const next = catalog.map((s) => ({
            ...s,
            value: byId.has(s.idPart) ? byId.get(s.idPart) : ""
          }));
          hydratingRef.current = false;
          setDraftSections(next);
          setEditorKey((k) => k + 1);
          message.success("Типовая форма загружена");
        } catch (e) {
          message.error(e.message || "Не удалось загрузить типовую форму");
          return Promise.reject(e);
        } finally {
          setTemplateLoading(false);
        }
      }
    });
  };

  /** Как BtnSaveDefault: сохранить текущий черновик как типовой (id_client=0). */
  const handleSaveTemplate = () => {
    Modal.confirm({
      title: "Сохранить как типовую?",
      content: "Текущий текст разделов станет типовой формой для всех клиентов.",
      okText: "Сохранить",
      cancelText: "Отмена",
      onOk: async () => {
        setTemplateLoading(true);
        try {
          const payload = catalog.map((s) => {
            const draft = draftSections.find((d) => d.idPart === s.idPart);
            return { idPart: s.idPart, value: draft?.value ?? "" };
          });
          await saveHealthHistoryTemplate(payload);
          setTemplateAvailable(true);
          message.success("Типовая форма сохранена");
        } catch (e) {
          message.error(e.message || "Не удалось сохранить типовую форму");
          return Promise.reject(e);
        } finally {
          setTemplateLoading(false);
        }
      }
    });
  };

  if (!idClient) {
    return <div className="client-tab client-tab--placeholder">Выберите клиента</div>;
  }

  if (loading) {
    return (
      <div className="client-tab client__center">
        <Spin tip="Загрузка..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-tab client-history">
        <Alert
          type="error"
          showIcon
          message={error}
          action={<Button onClick={load}>Повторить</Button>}
          className="client-history__error"
        />
      </div>
    );
  }

  if (!catalog.length) {
    return (
      <div className="client-tab client-tab--placeholder">
        Нет разделов истории болезни (справочник SPR_CL_PART_NAME пуст)
      </div>
    );
  }

  return (
    <div className="client-tab client-history">
      <div className="client-history__toolbar">
        <div className="client-history__actions">
          <Button type="primary" loading={saving} disabled={!dirty} onClick={handleSave}>
            Сохранить
          </Button>
          <Button
            icon={<DownloadOutlined />}
            disabled={saving || templateLoading || !templateAvailable}
            loading={templateLoading}
            onClick={handleLoadTemplate}
          >
            Типовая
          </Button>
          <Button
            icon={<SaveOutlined />}
            disabled={saving || templateLoading}
            onClick={handleSaveTemplate}
          >
            Как типовую
          </Button>
          <Button disabled={!dirty || saving} onClick={handleCancel}>
            Отменить
          </Button>
        </div>
        <div className="client-history__hint">
          Разделы фиксированы. Фразы — от 2 букв или <code>/</code>
        </div>
      </div>

      <div className="client-history__editor">
        <ClientHistoryEditor
          key={editorKey}
          sections={draftSections.length ? draftSections : catalog}
          onChange={handleEditorChange}
        />
      </div>
    </div>
  );
};

export default ClientHistory;
